import { useState, useEffect, useRef } from 'react'
import { useConversation } from '@elevenlabs/react'
import { useAuthStore } from '../hooks/useAuthStore'

/* ── Design tokens ──────────────────────────────────────── */
const C = {
  primary:            '#4800b2',
  primaryContainer:   '#6200ee',
  onPrimary:          '#ffffff',
  primaryFixed:       '#e8ddff',
  surface:            '#f8f9fd',
  surfaceContainerLowest: '#ffffff',
  surfaceContainerLow:    '#f2f3f7',
  surfaceContainerHigh:   '#e7e8ec',
  secondaryFixed:     '#e3e0f4',
  onSurface:          '#191c1f',
  onSurfaceVariant:   '#494456',
  outline:            '#7a7488',
  outlineVariant:     '#cbc3d9',
  secondary:          '#5e5d6d',
  error:              '#ba1a1a',
  errorContainer:     '#ffdad6',
}

export default function MyVoiceAgent() {
  const { user } = useAuthStore()
  const [connectionStatus, setConnectionStatus] = useState('disconnected')
  const [isSpeaking, setIsSpeaking]         = useState(false)
  const [isUserSpeaking, setIsUserSpeaking] = useState(false)
  const [error, setError]                   = useState('')
  const [conversationLog, setConversationLog] = useState([])
  const [micPermission, setMicPermission]   = useState('unknown')
  const [sessionSeconds, setSessionSeconds] = useState(0)
  const transcriptRef = useRef(null)
  const timerRef      = useRef(null)

  const conversation = useConversation({
    onConnect:    () => { setConnectionStatus('connected'); setError(''); setSessionSeconds(0) },
    onDisconnect: () => { setConnectionStatus('disconnected'); setIsSpeaking(false); setIsUserSpeaking(false) },
    onMessage: (message) => {
      const messageText = message.message || message.text || ''
      const messageRole = message.role || message.type || 'unknown'
      if (messageText) {
        setConversationLog(prev => [...prev, {
          id: `${Date.now()}-${Math.random()}`,
          role: messageRole, text: messageText, timestamp: new Date(),
        }])
      }
    },
    onError: (err) => { setError(err.message || 'An error occurred'); setConnectionStatus('disconnected') },
    onModeChange: (mode) => {
      setIsSpeaking(mode.mode === 'speaking')
      setIsUserSpeaking(mode.mode === 'listening')
    },
  })

  useEffect(() => { checkMicrophonePermission() }, [])

  useEffect(() => {
    if (connectionStatus === 'connected') {
      timerRef.current = setInterval(() => setSessionSeconds(s => s + 1), 1000)
    } else {
      clearInterval(timerRef.current)
    }
    return () => clearInterval(timerRef.current)
  }, [connectionStatus])

  useEffect(() => {
    if (transcriptRef.current)
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight
  }, [conversationLog])

  const checkMicrophonePermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      stream.getTracks().forEach(t => t.stop())
      setMicPermission('granted')
    } catch {
      setMicPermission('denied')
      setError('Microphone access is required')
    }
  }

  const handleStartConversation = async () => {
    if (micPermission !== 'granted') {
      await checkMicrophonePermission()
      if (micPermission !== 'granted') return
    }
    try {
      setConnectionStatus('connecting'); setError('')
      const agentId = import.meta.env.VITE_ELEVENLABS_AGENT_ID
      const apiKey  = import.meta.env.VITE_ELEVENLABS_API_KEY
      if (!agentId || !apiKey) throw new Error('ElevenLabs credentials not configured')
      await conversation.startSession({ agentId })
    } catch (err) { setError(err.message || 'Failed to start'); setConnectionStatus('disconnected') }
  }

  const handleEndConversation   = async () => { try { await conversation.endSession() } catch {} }
  const handleClearTranscript   = () => setConversationLog([])

  const handleDownloadTranscript = () => {
    if (!conversationLog.length) return
    const text = conversationLog.map(l =>
      `[${l.timestamp.toLocaleString()}] ${l.role === 'user' ? 'Patient' : 'AI Doctor'}: ${l.text}`
    ).join('\n\n')
    const a = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(new Blob([text], { type: 'text/plain' })),
      download: `voice-consultation-${new Date().toISOString().split('T')[0]}.txt`,
    })
    document.body.appendChild(a); a.click(); document.body.removeChild(a)
  }

  const handleCopyTranscript = async () => {
    const text = conversationLog.map(l =>
      `[${l.timestamp.toLocaleString()}] ${l.role === 'user' ? 'Patient' : 'AI Doctor'}: ${l.text}`
    ).join('\n\n')
    try { await navigator.clipboard.writeText(text); alert('Copied!') } catch { alert('Failed to copy') }
  }

  /* ── Derived ──────────────────────────────────────────── */
  const isConnected    = connectionStatus === 'connected'
  const isConnecting   = connectionStatus === 'connecting'
  const isDisconnected = connectionStatus === 'disconnected'

  const fmtTime = (s) => {
    const m = Math.floor(s / 60)
    const sec = s % 60
    return `${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`
  }

  const orbColor = isSpeaking
    ? `linear-gradient(135deg, ${C.primary}, ${C.primaryContainer})`
    : isUserSpeaking
    ? 'linear-gradient(135deg, #16a34a, #22c55e)'
    : isConnected
    ? `linear-gradient(135deg, ${C.primary}, ${C.primaryContainer})`
    : 'linear-gradient(135deg, #94a3b8, #64748b)'

  const statusText =
    isSpeaking     ? 'AI is speaking…'         :
    isUserSpeaking ? 'Listening to you…'        :
    isConnected    ? 'Dr. Sam is ready'         :
    isConnecting   ? 'Connecting…'              :
    'Voice Agent — Ready'

  const statusSub =
    isSpeaking     ? 'Processing your request in real-time.' :
    isUserSpeaking ? 'Speak clearly near your microphone.'   :
    isConnected    ? 'Speak naturally with your AI doctor.'  :
    isConnecting   ? 'Please wait while we connect…'         :
    'Click "Start Session" to begin your voice consultation.'

  return (
    <div style={{
      fontFamily: "'Inter','Segoe UI',sans-serif",
      color: C.onSurface,
    }}>

      {/* ── Two-column layout ─────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0,1fr) 340px',
        gap: 20,
        alignItems: 'start',
      }}>

        {/* ── LEFT: Voice interface ─────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Main orb card */}
          <div style={{
            background: C.surfaceContainerLowest,
            borderRadius: 28,
            boxShadow: '0 8px 40px rgba(72,0,178,0.08)',
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '56px 32px 48px',
            minHeight: 420,
          }}>
            {/* Radial gradient bg */}
            <div style={{
              position: 'absolute', inset: 0,
              background: `radial-gradient(circle at center, rgba(72,0,178,0.06) 0%, transparent 70%)`,
              pointerEvents: 'none',
            }} />

            {/* Pulse rings + orb */}
            <div style={{ position: 'relative', width: 256, height: 256, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 36 }}>
              {/* Outer pulse ring */}
              {isConnected && (
                <div style={{
                  position: 'absolute', inset: 0, borderRadius: '50%',
                  background: `${C.primaryContainer}22`,
                  animation: 'voicePing 1.8s cubic-bezier(0,0,0.2,1) infinite',
                }} />
              )}
              {/* Inner pulse */}
              {isConnected && (
                <div style={{
                  position: 'absolute', inset: 16, borderRadius: '50%',
                  background: `${C.primaryContainer}30`,
                  animation: 'voicePulse 2s ease-in-out infinite',
                }} />
              )}
              {/* Core orb */}
              <div style={{
                width: 192, height: 192, borderRadius: '50%',
                background: orbColor,
                boxShadow: isConnected ? `0 0 60px -15px rgba(72,0,178,0.55)` : 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                position: 'relative', overflow: 'hidden',
                transition: 'all 0.4s ease',
              }}>
                {/* Wave bars inside orb (visible when speaking) */}
                {(isSpeaking || isUserSpeaking) ? (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    maskImage: 'radial-gradient(circle, black 40%, transparent 80%)',
                    WebkitMaskImage: 'radial-gradient(circle, black 40%, transparent 80%)',
                  }}>
                    {[64, 96, 80, 128, 80, 96, 64].map((h, i) => (
                      <div key={i} style={{
                        width: 6, height: h, borderRadius: 3,
                        background: i % 2 === 0 ? 'rgba(255,255,255,0.45)' : 'rgba(255,255,255,0.75)',
                        animation: `waveBar${i % 3} ${0.6 + i * 0.1}s ease-in-out infinite alternate`,
                      }} />
                    ))}
                  </div>
                ) : (
                  <i className="uil uil-microphone" style={{ fontSize: 72, color: 'rgba(255,255,255,0.85)' }} />
                )}
              </div>
            </div>

            {/* Status */}
            {isConnected && (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                background: `${C.primaryContainer}15`,
                borderRadius: 9999, padding: '5px 14px', marginBottom: 16,
              }}>
                <span style={{
                  width: 8, height: 8, borderRadius: '50%',
                  background: C.primaryContainer,
                  animation: 'voicePulse 1.2s ease-in-out infinite',
                  display: 'inline-block',
                }} />
                <span style={{ fontSize: 12, fontWeight: 700, color: C.primary, letterSpacing: 0.5 }}>
                  {isSpeaking ? 'Speaking' : isUserSpeaking ? 'Listening' : 'Active'}
                </span>
              </div>
            )}

            <h2 style={{
              fontSize: 28, fontWeight: 800,
              fontFamily: "'Manrope','Inter',sans-serif",
              color: C.onSurface, margin: '0 0 10px',
              textAlign: 'center',
            }}>{statusText}</h2>

            <p style={{
              fontSize: 14, color: C.onSurfaceVariant,
              maxWidth: 360, textAlign: 'center', lineHeight: 1.6, margin: '0 0 28px',
            }}>{statusSub}</p>

            {/* Error */}
            {error && (
              <div style={{
                background: '#fef2f2', border: '1px solid #fecaca',
                borderRadius: 12, padding: '10px 18px', marginBottom: 20,
                display: 'flex', alignItems: 'center', gap: 8,
                color: C.error, fontSize: 13,
              }}>
                <i className="uil uil-exclamation-circle" style={{ fontSize: 16 }} />
                {error}
                {micPermission === 'denied' && (
                  <button onClick={checkMicrophonePermission} style={{
                    marginLeft: 8, padding: '2px 10px', fontSize: 12,
                    border: `1px solid ${C.error}`, borderRadius: 8,
                    background: 'none', color: C.error, cursor: 'pointer',
                  }}>Retry</button>
                )}
              </div>
            )}

            {/* Controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
              {isConnected && (
                <button
                  title="Mute microphone"
                  style={{
                    width: 56, height: 56, borderRadius: '50%',
                    background: C.surfaceContainerHigh,
                    border: 'none', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                  }}
                >
                  <i className="uil uil-microphone-slash" style={{ fontSize: 22, color: C.onSurfaceVariant }} />
                </button>
              )}

              {isDisconnected ? (
                <button
                  onClick={handleStartConversation}
                  disabled={micPermission === 'denied'}
                  style={{
                    width: 84, height: 84, borderRadius: '50%',
                    background: micPermission === 'denied' ? C.outline : C.primary,
                    border: '4px solid #fff',
                    boxShadow: micPermission === 'denied' ? 'none' : '0 8px 24px rgba(72,0,178,0.4)',
                    cursor: micPermission === 'denied' ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.15s',
                  }}
                >
                  <i className="uil uil-microphone" style={{ fontSize: 36, color: '#fff' }} />
                </button>
              ) : isConnecting ? (
                <button disabled style={{
                  width: 84, height: 84, borderRadius: '50%',
                  background: C.primary, border: '4px solid #fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  opacity: 0.8,
                }}>
                  <span style={{
                    width: 24, height: 24, border: '3px solid rgba(255,255,255,0.3)',
                    borderTop: '3px solid #fff', borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite', display: 'inline-block',
                  }} />
                </button>
              ) : (
                <button
                  onClick={handleEndConversation}
                  style={{
                    width: 84, height: 84, borderRadius: '50%',
                    background: C.error, border: '4px solid #fff',
                    boxShadow: '0 8px 24px rgba(186,26,26,0.35)',
                    cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <i className="uil uil-times" style={{ fontSize: 36, color: '#fff' }} />
                </button>
              )}

              {isConnected && (
                <button
                  title="Pause"
                  style={{
                    width: 56, height: 56, borderRadius: '50%',
                    background: C.surfaceContainerHigh,
                    border: 'none', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                  }}
                >
                  <i className="uil uil-pause" style={{ fontSize: 22, color: C.onSurfaceVariant }} />
                </button>
              )}
            </div>

            {/* Start label */}
            {isDisconnected && (
              <p style={{ marginTop: 12, fontSize: 12, color: C.outline }}>
                <i className="uil uil-info-circle" style={{ marginRight: 4 }} />
                Microphone access required
              </p>
            )}
          </div>

          {/* Session summary bar */}
          {isConnected && (
            <div style={{
              background: C.surfaceContainerLow,
              borderRadius: 18,
              padding: '16px 24px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              border: `1px solid ${C.outlineVariant}22`,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    background: C.surfaceContainerLowest, borderRadius: 12,
                    width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                  }}>
                    <i className="uil uil-clock" style={{ fontSize: 20, color: C.primary }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: C.outline, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Session Duration</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: C.onSurface, fontFamily: "'Manrope',sans-serif" }}>
                      {fmtTime(sessionSeconds)}
                    </div>
                  </div>
                </div>
                <div style={{ width: 1, height: 32, background: C.outlineVariant }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    background: C.surfaceContainerLowest, borderRadius: 12,
                    width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                  }}>
                    <i className="uil uil-comment-alt-lines" style={{ fontSize: 20, color: C.primary }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: C.outline, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Messages</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: C.onSurface, fontFamily: "'Manrope',sans-serif" }}>
                      {conversationLog.length}
                    </div>
                  </div>
                </div>
              </div>
              <button
                onClick={handleEndConversation}
                style={{
                  padding: '10px 24px', borderRadius: 9999,
                  background: C.errorContainer, color: '#93000a',
                  border: 'none', fontWeight: 700, fontSize: 13,
                  cursor: 'pointer', fontFamily: "'Inter',sans-serif",
                }}
              >End Session</button>
            </div>
          )}

          {/* Disclaimer */}
          <div style={{
            background: C.surfaceContainerHigh + '80',
            borderRadius: 12, padding: '12px 16px',
            display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 11,
          }}>
            <i className="uil uil-info-circle" style={{ fontSize: 16, color: C.error, flexShrink: 0, marginTop: 1 }} />
            <p style={{ margin: 0, color: C.onSurfaceVariant, lineHeight: 1.7 }}>
              <strong style={{ color: C.onSurface }}>Medical Disclaimer: </strong>
              This AI voice assistant provides general medical information only — not a substitute for professional medical advice. In emergencies, call your local emergency services immediately.
            </p>
          </div>
        </div>

        {/* ── RIGHT: Live Transcript ───────────────────────── */}
        <div style={{
          background: C.surfaceContainerLowest,
          borderRadius: 18,
          border: '1px solid #f1f0f5',
          boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'calc(100vh - 120px)',
          minHeight: 480,
          overflow: 'hidden',
          position: 'sticky',
          top: 24,
        }}>
          {/* Transcript header */}
          <div style={{
            padding: '16px 20px',
            borderBottom: '1px solid #f1f0f5',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className="uil uil-subject" style={{ fontSize: 18, color: C.primaryContainer }} />
              <span style={{ fontWeight: 700, fontSize: 13, color: C.onSurface, fontFamily: "'Manrope',sans-serif" }}>
                Live Transcript
              </span>
            </div>
            <span style={{
              fontSize: 9, fontWeight: 700, padding: '2px 8px',
              borderRadius: 4, letterSpacing: 1,
              background: isConnected ? '#dcfce7' : C.surfaceContainerHigh,
              color: isConnected ? '#16a34a' : C.outline,
              textTransform: 'uppercase',
            }}>
              {isConnected ? 'LIVE' : 'IDLE'}
            </span>
          </div>

          {/* Transcript body */}
          <div
            ref={transcriptRef}
            style={{
              flex: 1, overflowY: 'auto', padding: '16px',
              scrollbarWidth: 'thin', scrollbarColor: `${C.outlineVariant} transparent`,
            }}
          >
            {conversationLog.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                {conversationLog.map(log => {
                  const isUser = log.role === 'user' || log.role === 'user_message'
                  return (
                    <div key={log.id} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      }}>
                        <span style={{
                          fontSize: 9, fontWeight: 700,
                          color: isUser ? C.primary : C.secondary,
                          textTransform: 'uppercase', letterSpacing: 1,
                        }}>
                          {isUser ? 'You' : 'AI Doctor'}
                        </span>
                        <span style={{ fontSize: 9, color: '#94a3b8' }}>
                          {log.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <div style={{
                        padding: '10px 14px',
                        background: isUser ? C.surfaceContainerLow : C.surfaceContainerLowest,
                        border: isUser ? 'none' : '1px solid #f1f0f5',
                        borderRadius: '0 12px 12px 12px',
                        fontSize: 12, color: isUser ? C.onSurface : C.onSurfaceVariant,
                        lineHeight: 1.6,
                      }}>
                        {log.text}
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div style={{
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                height: '100%', minHeight: 200,
                color: C.outline, textAlign: 'center',
              }}>
                <i className="uil uil-subject" style={{ fontSize: 32, marginBottom: 8 }} />
                <p style={{ margin: 0, fontSize: 12, fontWeight: 500 }}>
                  Start a session to see the transcript here
                </p>
              </div>
            )}
          </div>

          {/* Transcript footer */}
          <div style={{
            padding: '12px 16px',
            background: C.surfaceContainerLow,
            borderTop: '1px solid #f1f0f5',
            display: 'flex', gap: 8,
          }}>
            <button
              onClick={handleCopyTranscript}
              disabled={conversationLog.length === 0}
              style={{
                flex: 1, padding: '8px 0', fontSize: 11, fontWeight: 700,
                background: C.surfaceContainerLowest,
                border: `1px solid ${C.outlineVariant}`,
                borderRadius: 8, cursor: conversationLog.length ? 'pointer' : 'default',
                color: conversationLog.length ? C.onSurface : C.outline,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
              }}
            >
              <i className="uil uil-clipboard" style={{ fontSize: 13 }} />
              Copy
            </button>
            <button
              onClick={handleDownloadTranscript}
              disabled={conversationLog.length === 0}
              style={{
                flex: 1, padding: '8px 0', fontSize: 11, fontWeight: 700,
                background: C.surfaceContainerLowest,
                border: `1px solid ${C.outlineVariant}`,
                borderRadius: 8, cursor: conversationLog.length ? 'pointer' : 'default',
                color: conversationLog.length ? C.onSurface : C.outline,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
              }}
            >
              <i className="uil uil-download-alt" style={{ fontSize: 13 }} />
              Export
            </button>
            {conversationLog.length > 0 && (
              <button
                onClick={handleClearTranscript}
                style={{
                  padding: '8px 12px', fontSize: 11, fontWeight: 700,
                  background: C.surfaceContainerLowest,
                  border: `1px solid ${C.outlineVariant}`,
                  borderRadius: 8, cursor: 'pointer',
                  color: C.error,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <i className="uil uil-trash-alt" style={{ fontSize: 13 }} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── CSS keyframes ─────────────────────────────────── */}
      <style>{`
        @keyframes voicePing {
          75%, 100% { transform: scale(1.4); opacity: 0; }
        }
        @keyframes voicePulse {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50%       { opacity: 0.7; transform: scale(1.05); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes waveBar0 {
          from { height: 40px; } to { height: 80px; }
        }
        @keyframes waveBar1 {
          from { height: 70px; } to { height: 110px; }
        }
        @keyframes waveBar2 {
          from { height: 55px; } to { height: 95px; }
        }
      `}</style>
    </div>
  )
}